import React from 'react';
import {
  PiClockLight,
  PiListChecksLight,
  PiLaptopLight,
  PiCalendarCheckLight,
  PiCertificateLight,
  PiGraduationCapLight,
  PiCheckCircleLight,
} from 'react-icons/pi';
import { programmes } from '../data/programmes';
import Cta from '../components/Cta';
import Reveal from '../components/Reveal';

const delivery = [
  {
    icon: PiLaptopLight,
    title: '100% online',
    body: 'Structured lessons, learning materials and assessments delivered entirely online, so you can study around your existing commitments.',
  },
  {
    icon: PiCalendarCheckLight,
    title: '+5 days for assignments',
    body: 'Every programme adds five days after the learning period to complete and submit assignments and finalise outstanding coursework.',
  },
  {
    icon: PiCertificateLight,
    title: 'Marked by qualified assessors',
    body: 'Written quizzes and assignments are submitted online via WhatsApp or email and marked by qualified assessors.',
  },
];

const CoursesPage: React.FC = () => {
  return (
    <section
      id="courses"
      className="min-h-[100dvh] bg-mesh-warm px-4 pb-32 pt-36 sm:px-8 sm:pt-44 lg:px-12"
    >
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <span className="eyebrow bg-sage-pale text-sage-deep">Three programmes</span>
          <h1 className="mt-7 max-w-4xl font-display text-[3rem] font-light leading-[0.98] tracking-[-0.035em] text-espresso-900 sm:text-7xl lg:text-8xl">
            Our Training Programmes
          </h1>
          <p className="mt-8 max-w-2xl text-[15px] leading-relaxed text-espresso-600">
            Leolilian Care International is committed to providing accessible, flexible and
            career-focused foundation training designed to equip learners with essential
            knowledge, skills and professional understanding for their chosen fields. We
            currently offer three specialised online training programmes.
          </p>
        </Reveal>

        {/* How it works */}
        <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-3">
          {delivery.map(({ icon: Icon, title, body }, i) => (
            <Reveal key={title} delay={i * 90}>
              <div className="bezel-shell h-full">
                <div className="bezel-core h-full p-7">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sage-pale text-sage-deep">
                    <Icon className="h-5 w-5" strokeWidth={1} />
                  </span>
                  <h2 className="mt-8 font-display text-xl font-light tracking-tight text-espresso-900">
                    {title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-espresso-600">{body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Programmes — full module breakdown */}
        <div className="mt-24 space-y-4">
          {programmes.map(({ icon: Icon, title, duration, hours, summary, modules }, i) => (
            <Reveal key={title} delay={i * 80}>
              <article className="bezel-shell-lg group transition-all duration-700 ease-fluid hover:shadow-lifted">
                <div className="bezel-core-lg grid gap-10 p-8 sm:p-12 lg:grid-cols-12">
                  <div className="lg:col-span-5">
                    <div className="flex items-start justify-between gap-6">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sage-pale text-sage-deep transition-transform duration-700 ease-fluid group-hover:scale-105">
                        <Icon className="h-5 w-5" strokeWidth={1} />
                      </span>
                      <span className="eyebrow bg-espresso-700/[0.05] text-espresso-600">
                        Programme {i + 1}
                      </span>
                    </div>

                    <h2 className="mt-8 font-display text-3xl font-light leading-tight tracking-tight text-espresso-900 sm:text-4xl">
                      {title}
                    </h2>
                    <p className="mt-5 text-[15px] leading-relaxed text-espresso-600">
                      {summary}
                    </p>

                    <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
                      <span className="inline-flex items-center gap-2 text-xs text-espresso-500">
                        <PiClockLight className="h-4 w-4" strokeWidth={1} />
                        {duration}
                        {hours ? ` · ${hours}` : ''}
                      </span>
                      <span className="inline-flex items-center gap-2 text-xs text-espresso-500">
                        <PiListChecksLight className="h-4 w-4" strokeWidth={1} />
                        {modules.length} modules
                      </span>
                    </div>

                    <p className="mt-6 text-xs leading-relaxed text-espresso-500">
                      + 5 additional days for assignment completion and submission.
                    </p>

                    <div className="mt-8">
                      <Cta to="/enroll" variant="ghost">
                        Enroll in this programme
                      </Cta>
                    </div>
                  </div>

                  {/* Module list */}
                  <div className="lg:col-span-7">
                    <p className="text-[10px] uppercase tracking-eyebrow text-espresso-500">
                      Modules
                    </p>
                    <ol className="mt-5 grid gap-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                      {modules.map((module, n) => (
                        <li
                          key={module}
                          className="flex items-start gap-3 rounded-2xl px-3 py-2.5 transition-colors duration-700 ease-fluid hover:bg-espresso-700/[0.04]"
                        >
                          <span className="mt-px w-5 shrink-0 text-xs tabular-nums text-espresso-500">
                            {String(n + 1).padStart(2, '0')}
                          </span>
                          <span className="text-sm text-espresso-800">{module}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {/* Assessment method */}
        <div className="mt-24">
          <Reveal>
            <span className="eyebrow bg-plum-pale text-plum-deep">Assessment</span>
            <h2 className="mt-6 max-w-3xl font-display text-4xl font-light leading-[1.05] tracking-tight text-espresso-900 sm:text-5xl">
              How you are assessed
            </h2>
            <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-espresso-600">
              All theory training and assessment are conducted 100% online.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-12">
            <Reveal className="md:col-span-5">
              <div className="bezel-shell h-full">
                <div className="bezel-core h-full p-8">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-espresso-700/[0.05] text-espresso-700">
                    <PiCheckCircleLight className="h-5 w-5" strokeWidth={1} />
                  </span>
                  <h3 className="mt-8 font-display text-2xl font-light tracking-tight text-espresso-900">
                    Theory
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-espresso-600">
                    Written quizzes and assignments submitted online via WhatsApp or email,
                    marked by qualified assessors.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={90} className="md:col-span-7">
              <div className="bezel-shell h-full">
                <div className="bezel-core h-full p-8">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sage-pale text-sage-deep">
                    <PiGraduationCapLight className="h-5 w-5" strokeWidth={1} />
                  </span>
                  <h3 className="mt-8 font-display text-2xl font-light tracking-tight text-espresso-900">
                    Practical — Caregiver Course
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-espresso-600">
                    Upon successful completion of theory and receipt of certificate, learners
                    are placed at linked home facilities for practical experience. On
                    completion, the facility issues a recommendation letter to confirm
                    competence.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={120}>
          <div className="bezel-shell-lg mt-6">
            <div className="bezel-core-lg flex flex-col items-center justify-between gap-8 bg-espresso-800 px-8 py-14 text-cream sm:flex-row sm:px-14">
              <h2 className="max-w-lg font-display text-3xl font-light leading-tight tracking-tight sm:text-4xl">
                Ready to start your programme?
              </h2>
              <Cta to="/enroll" variant="light">
                Start your enrolment
              </Cta>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default CoursesPage;
